import { inject, Injectable } from "@angular/core";
import { FgdsProject } from "@domain/project/fgds-project";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { BusinessScope } from "@lib/constants/business-scope";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { DraegerFonts } from "@pdf/helpers/draeger-fonts";
import { FontNames, PdfProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { defer, map, Observable, of, tap } from "rxjs";
import { FloorplanInformationPdfService } from "src/domain/pdf/export-services/product-information-pages/floorplan-information-pages/floorplan-information-pdf-service";
import { ProductListPdfService } from "./product-list-pages/product-list-pdf.service";
import { ProductNotesPdfService } from "./product-notes-pages/product-notes-pdf.service";

@Injectable({
  providedIn: "root",
})
export class ProductInformationPagesService {
  private productListPdfService = inject(ProductListPdfService);
  private productNotesPdfService = inject(ProductNotesPdfService);
  private floorplanInformationService = inject(FloorplanInformationPdfService);

  generateFgdsProductList(pdf: jsPDF, project: FgdsProject, tableOfContents: TableOfContents): Observable<jsPDF> {
    return defer(() => {
      this.addFonts(pdf);
      pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);

      const container = new HalfPageContainer();

      return this.productListPdfService
        .generateFgdsProductList(pdf, project, project.cachedFloorplans, container, tableOfContents, true, true)
        .pipe(map(() => pdf));
    });
  }

  generateMonitoringProductList(pdf: jsPDF, project: MonitoringProject, tableOfContents: TableOfContents): Observable<jsPDF> {
    return defer(() => {
      this.addFonts(pdf);
      pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      const container = new HalfPageContainer();
      return this.productListPdfService
        .generateMonitoringProductList(pdf, project, project.cachedFloorplans, container, tableOfContents, true)
        .pipe(map(() => pdf));
    });
  }

  generateForFloorplan(
    pdf: jsPDF,
    floorplan: Floorplan,
    tableOfContents: TableOfContents,
    shouldAddProductList = true,
    scope: BusinessScope,
  ): Observable<{ pdf: jsPDF; tableOfContents: TableOfContents }> {
    return defer(() => {
      this.preparePdf(pdf);
      const container = new HalfPageContainer();

      return this.buildProductList$(pdf, floorplan, container, tableOfContents, shouldAddProductList, scope).pipe(
        tap(() => this.appendFloorplanPages(pdf, floorplan, container, tableOfContents)),
        map(() => ({ pdf, tableOfContents })),
      );
    });
  }

  private appendFloorplanPages(
    pdf: jsPDF,
    floorplan: Floorplan,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
  ): void {
    this.productNotesPdfService.generatePages(pdf, floorplan, container, tableOfContents);
    this.floorplanInformationService.generatePages(pdf, floorplan, container, tableOfContents);
  }

  private buildProductList$(
    pdf: jsPDF,
    floorplan: Floorplan,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    shouldAddProductList: boolean,
    scope: BusinessScope,
  ): Observable<void> {
    if (!shouldAddProductList) {
      return of(void 0);
    }

    switch (scope) {
      case BusinessScope.FGDS:
        if (!isFgdsProject(floorplan.project)) {
          return of(void 0);
        }
        return this.productListPdfService.generateFgdsProductList(
          pdf,
          floorplan.project,
          [floorplan],
          container,
          tableOfContents,
          false,
          false,
        );

      case BusinessScope.MONITORING:
        if (!isMonitoringProject(floorplan.project)) {
          return of(void 0);
        }
        return this.productListPdfService.generateMonitoringProductList(
          pdf,
          floorplan.project,
          [floorplan],
          container,
          tableOfContents,
        );

      default:
        return of();
    }
  }

  private preparePdf(pdf: jsPDF): void {
    this.addFonts(pdf);
    pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
    pdf.setTextColor(PdfProperties.DRAEGERBLUE);
  }

  private addFonts(doc: jsPDF) {
    new DraegerFonts().fonts.forEach((font) => {
      doc.addFileToVFS(font.ttfName, font.base64);
      doc.addFont(font.ttfName, font.fontName, font.fontWeight);
    });
  }
}
